import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import { Dropdown as AntdDropdown } from 'antd';
import { useSwitch } from '@/hooks';
import ReactGA4 from 'react-ga4';
import { Checkbox, HelperTooltip, Tips } from '@/components';
import { IconRcClearFilter, IconRcSelectDownArrow } from '@/assets/stream';
import { isArray } from 'lodash';
import clsx from 'clsx';
import {
  TypesOptions,
  UsdValueOptions,
  CreateAtOptions,
  TypesMap,
  UsdValueMap,
  CreateAtMap,
} from './const';
import { useInitPostFilter, usePostFilter } from './hooks';

import styles from './index.module.scss';

export const PostFilter = () => {
  const { t } = useTranslation();

  const { filters, setPostFilter, clearFilter } = usePostFilter();
  useInitPostFilter();

  const handleMenuOptionClick = useCallback(
    (filterType: 'Type' | 'Assets' | 'Time', val: string) => {
      if (filterType === 'Type' && !filters?.types?.includes(val)) {
        ReactGA4.event({
          category: 'Stream Search Usage',
          action: `Stream_Filter_Type_${TypesMap[val]}`,
        });
        return;
      }

      if (filterType === 'Assets' && filters?.creator_usd_value !== val) {
        ReactGA4.event({
          category: 'Stream Search Usage',
          action: `Stream_Filter_Assets_${UsdValueMap[val]}`,
        });
        return;
      }

      if (filterType === 'Time' && filters?.create_at !== val) {
        ReactGA4.event({
          category: 'Stream Search Usage',
          action: `Stream_Filter_Time_${CreateAtMap[val]}`,
        });
        return;
      }
    },
    [filters?.create_at, filters?.creator_usd_value, filters?.types],
  );
  return (
    <div className={styles.filterContainer}>
      <div className={styles.filterItem}>
        <FilterSelect
          placeholder={t('StreamFilter.1v1yqi')}
          filterOptions={TypesOptions}
          multiple
          value={filters.types}
          onChange={val => setPostFilter({ ...filters, types: val as string[] })}
          dropdownWidth={175}
          pickOnly="prime_pick"
          onMenuClick={val => handleMenuOptionClick('Type', val)}
        />
      </div>
      <div className={styles.filterItem}>
        <FilterSelect
          placeholder={t('StreamFilter.yplnyu')}
          filterOptions={UsdValueOptions}
          value={filters.creator_usd_value}
          onChange={val => setPostFilter({ ...filters, creator_usd_value: val as string })}
          dropdownWidth={135}
          onMenuClick={val => handleMenuOptionClick('Assets', val)}
        />
      </div>
      <div className={styles.filterItem}>
        <FilterSelect
          placeholder={t('StreamFilter.1jvfx')}
          filterOptions={CreateAtOptions}
          value={filters.create_at}
          onChange={val => setPostFilter({ ...filters, create_at: val as string })}
          dropdownWidth={125}
          onMenuClick={val => handleMenuOptionClick('Time', val)}
        />
      </div>
      <HelperTooltip title={t('StreamFilter.krvd3d')} placement="bottom">
        <div className={styles.clearFilter} onClick={() => clearFilter()}>
          <IconRcClearFilter />
        </div>
      </HelperTooltip>
    </div>
  );
};

type FilterOption = {
  label: React.JSX.Element | string;
  value: string;
  children?: FilterOption[];
  tip?: React.JSX.Element | string;
};

export const FilterSelect = ({
  placeholder,
  filterOptions,
  multiple,
  value,
  onChange,
  dropdownWidth,
  pickOnly,
  onMenuClick,
}: {
  placeholder: string;
  filterOptions: FilterOption[];
  multiple?: boolean;
  value?: string | string[];
  onChange?: (val: string | string[]) => void;
  dropdownWidth?: number;
  pickOnly?: string;
  onMenuClick?: (val: string) => void;
}) => {
  const { on, turnOff, turn } = useSwitch();

  const displayLabel = useMemo(() => {
    if (isArray(value)) {
      const displayOptions = filterOptions.filter(i => value?.includes(i.value));
      return displayOptions?.map((i, index) => (
        <span key={i.value}>
          {i.label}
          {index === displayOptions.length - 1 ? '' : ', '}
        </span>
      ));
    }
    return filterOptions?.find(i => i.value === value)?.label;
  }, [filterOptions, value]);

  const handleClick = useCallback(
    (val: string) => {
      onMenuClick?.(val);
      if (multiple) {
        if (pickOnly && value?.includes(pickOnly) && val !== pickOnly) {
          //当前值包含pickOnly时，其他选项点击后清空pickOnly
          onChange?.([val]);
          return;
        }
        if (pickOnly && !value?.includes(pickOnly) && val === pickOnly) {
          // 如果选中了pickOnly，清空其他选项
          onChange?.([val]);
          return;
        }
        const newVal = isArray(value) ? value : [];
        if (value?.includes(val)) {
          onChange?.(newVal.filter(i => i !== val));
        } else {
          onChange?.([...newVal, val]);
        }
      } else {
        if (val === value) {
          onChange?.('');
        } else {
          onChange?.(val);
        }
      }
    },
    [multiple, onChange, onMenuClick, pickOnly, value],
  );

  const handleMenuClick = useCallback(() => {
    if (multiple) {
      return;
    }
    turnOff();
  }, [multiple, turnOff]);

  return (
    <>
      <AntdDropdown
        visible={on}
        onVisibleChange={turn}
        trigger={['click']}
        placement="bottomCenter"
        align={{ offset: [0, 8] }}
        getPopupContainer={dom => dom.parentElement!}
        overlay={
          <div
            className={styles.dropdownPanel}
            onClick={handleMenuClick}
            style={{ width: dropdownWidth ? `${dropdownWidth}px` : undefined }}>
            <div className={styles.dropdownList}>
              {filterOptions?.map(option => {
                const isSelected = isArray(value)
                  ? value.includes(option.value)
                  : option.value === value;
                return (
                  <div
                    className={clsx(styles.optionItem, isSelected && !multiple && styles.selected)}
                    onClick={() => handleClick(option.value)}
                    key={option.value}>
                    {multiple ? <Checkbox type="form" checked={isSelected} /> : null}
                    <div className={styles.optionLabel}>{option.label}</div>
                    {option?.tip ? <Tips title={option.tip} /> : null}
                  </div>
                );
              })}
            </div>
          </div>
        }>
        <div className={styles.select}>
          <div className={clsx(styles.selectLabel, displayLabel && styles.selected)}>
            {displayLabel ? displayLabel : placeholder}
          </div>
          <IconRcSelectDownArrow className={styles.icon} />
        </div>
      </AntdDropdown>
    </>
  );
};
